import type { ReactNode, CSSProperties } from 'react';
import clsx from 'clsx';
import { Icons } from '@/theme';
import { wrapTextChild } from '../wrapTextChild';
import { alertRecipe, alertIconSlot, alertContent, alertTitle, alertAction, alertCloseButton } from './alert.css';

export type AlertSeverity = 'error' | 'warning' | 'success' | 'info';

export interface AlertProps {
  severity?: AlertSeverity;
  /** Override the default severity icon. Pass null to suppress the icon. */
  icon?: ReactNode;
  /** Right-side action area (button, link, etc.) */
  action?: ReactNode;
  /** Shows a close button and calls this when clicked */
  onClose?: () => void;
  /** Bold title displayed above children */
  title?: string;
  children?: ReactNode;
  style?: CSSProperties;
  className?: string;
  'data-testid'?: string;
  /** A state a test or the staging harness reads (e.g. the consume completion banner's state). */
  'data-state'?: string;
  /** Set by the global banner for an opaque item (Q-530). */
  'data-opaque'?: string;
  role?: string;
}

const DEFAULT_ICONS: Record<AlertSeverity, ReactNode> = {
  error:   <Icons.error style={{ fontSize: 20 }} />,
  warning: <Icons.warning style={{ fontSize: 20 }} />,
  success: <Icons.success style={{ fontSize: 20 }} />,
  info:    <Icons.info style={{ fontSize: 20 }} />,
};

export function Alert({
  severity = 'info',
  icon,
  action,
  onClose,
  title,
  children,
  style,
  className,
  'data-testid': testId,
  'data-state': dataState,
  'data-opaque': dataOpaque,
  role,
}: AlertProps) {
  const iconContent = icon !== undefined ? icon : DEFAULT_ICONS[severity];

  return (
    <div
      className={clsx(alertRecipe({ severity }), className)}
      style={style}
      data-testid={testId}
      data-state={dataState}
      data-opaque={dataOpaque}
      role={role ?? 'alert'}
    >
      {iconContent && (
        <span className={alertIconSlot}>{iconContent}</span>
      )}
      <div className={alertContent}>
        {title && <div className={alertTitle}>{title}</div>}
        {wrapTextChild(children)}
      </div>
      {(action || onClose) && (
        <div className={alertAction}>
          {action != null && <span>{action}</span>}
          {onClose && (
            <button
              type="button"
              className={alertCloseButton}
              onClick={onClose}
              aria-label="Close"
            >
              <Icons.close style={{ fontSize: 16 }} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
